import { useState } from 'react';
import { useField, Formik, Form } from 'formik';
import './AccessCode.css';
import '../ContactForm.css';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSchool } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import FidgetLoader from '../FidgetLoader';
import { isSuccess } from '../../utils/generalUtils';
import { validateAccessCode } from '../../service/accessCodesService';
import PurchaseCard from '../PurchaseCard';

function TextInput({ label, ...props }) {
    const [field, meta] = useField(props);
    return (
        <>
            <div className="contact__label-container">
                <label htmlFor={props.id || props.name}>{label}</label>
                {meta.touched && meta.error ? (
                    <div className="error">{meta.error}</div>
                ) : null}
            </div>
            <input
                className="text-input"
                {...field}
                {...props} />
        </>
    );
}

function AccessCode() {
    const {
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        loginWithRedirect,
    } = useAuth0();

    const navigate = useNavigate();

    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const verifyAccessCode = async (accessCode) => {
        try {
            setSubmitLoading(true);
            setErrorMessage('');

            // get an access token for the /verify-access-code endpoint
            const token = await getAccessTokenSilently();

            // call backend to validate the access code
            const res = await validateAccessCode(accessCode, token);

            if (isSuccess(res)) {
                // force token refresh to update the user session
                await loginWithRedirect({
                    authorizationParams: {
                        prompt: 'none',
                    },
                    appState: {
                        returnTo: '/members',
                    },
                });
            } else {
                // if access code is invalid, show the error message and reset loading state
                setSubmitLoading(false);
                setErrorMessage('Invalid access code. Please try again.');
            }
        } catch (error) {
            // reset loading state if an error occurs
            setSubmitLoading(false);

            // show a generic error message in case of failure
            setErrorMessage(
                error.message
                    || 'An error occurred while verifying the access code.',
            );

            throw new Error('Error verifying access code:', error);
        }
    };

    if (isLoading || submitLoading) {
        return (
            <div className="fidget__container">
                <FidgetLoader />
            </div>
        );
    }

    return (
        <div className="login__outer-container">
            <div className="login__inner-container">
                <div className="contact__heading">Members Login</div>
                {isAuthenticated ? (
                    <Formik
                        initialValues={{
                            accessCode: '',
                        }}
                        validationSchema={Yup.object({
                            accessCode: Yup.string().required(
                                '* You must enter an access code',
                            ),
                        })}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitLoading(true);
                            setSubmitting(false);
                            verifyAccessCode(values.accessCode.toLowerCase());
                        }}>
                        <div className="contact__form-container">
                            <div className="contact__message">
                                Please enter your provided access code to verify your membership.
                                {errorMessage && (
                                    <p className="error" style={{ color: '#ff5b14' }}>
                                        *
                                        {errorMessage}
                                    </p>
                                )}
                            </div>
                            <Form className="contact__form">
                                <TextInput
                                    label="Access Code:"
                                    name="accessCode"
                                    type="text"
                                    placeholder="Enter your access code" />
                                <div className="contact__submit-container">
                                    <button
                                        className="contact__submit-button"
                                        type="submit"
                                        disabled={submitLoading}>
                                        Continue
                                    </button>
                                </div>
                            </Form>
                        </div>
                    </Formik>
                ) : (
                    <div className="contact__form-container">
                        <div style={{
                            fontWeight: 700, fontSize: '1.25rem', color: '#fff', marginBottom: 0, lineHeight: 1.3,
                        }}>
                            You must be logged in to access the Member's section.
                        </div>
                        <div
                            className="login-option-row"
                            style={{
                                display: 'flex', gap: 32, justifyContent: 'center', marginTop: 32,
                            }}>
                            <div
                                onClick={loginWithRedirect}
                                onKeyDown={loginWithRedirect}
                                className="login-option-box"
                                tabIndex={0}
                                role="button"
                                aria-label="User Login">
                                <FontAwesomeIcon
                                    icon={faUser}
                                    color="#fff"
                                    size="3x"
                                    style={{ marginBottom: 24 }} />
                                <span style={{
                                    fontWeight: 600, fontSize: '1.1rem', color: '#fff', letterSpacing: 0.5,
                                }}>
                                    User Login
                                </span>
                            </div>
                            <div
                                onClick={() => navigate('/school-login')}
                                onKeyDown={() => navigate('/school-login')}
                                className="login-option-box"
                                tabIndex={0}
                                role="button"
                                aria-label="School Login">
                                <FontAwesomeIcon
                                    icon={faSchool}
                                    color="#fff"
                                    size="3x"
                                    style={{ marginBottom: 24 }} />
                                <span style={{
                                    fontWeight: 600, fontSize: '1.1rem', color: '#fff', letterSpacing: 0.5,
                                }}>
                                    School Login
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                {!isAuthenticated && (
                    <div style={{ marginTop: 36, display: 'flex', justifyContent: 'center' }}>
                        <PurchaseCard
                            title="Don't have a STEAM Lab membership yet?"
                            className="purchase-card--loginselector" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccessCode;
